import React, { useEffect, useState } from 'react';

import { MemberEngagementLevel } from '@vestahealthcare/common/enums';
import Enum, {
  Selectable,
  SelectableInfo,
} from '@vestahealthcare/common/enums/Enum';
import { translate } from '@vestahealthcare/common/i18n';
import {
  Employee,
  FilterSelectable,
  Organization,
} from '@vestahealthcare/common/models';

import { Label, Panel, Select, SelectLanguage, TextInput } from 'styleguide-v2';
import { FilterGroup, FilterItem } from 'styleguide-v2/src/components/Panel';

import { ReferralDropdown } from 'dash/src/components/ReferralDropdown';
import {
  FiltersVestaTeam,
  VestaTeamKey,
} from 'dash/src/components/filters/FiltersVestaTeam';
import { GetVisitSummaryParams } from 'dash/src/services/VisitServices';
import { getFilterCount, getFilterItem } from 'dash/src/utils/filterUtils';

type KeyGetVisitSummaryParams = keyof GetVisitSummaryParams;

type Props = {
  data: {
    appointmentTypes: Selectable[];
    employees: SelectableInfo<Employee>[];
    languages: Selectable[];
    referralSources: SelectableInfo<Organization>[];
    pods: Selectable[];
  };
  filters?: {
    [x in KeyGetVisitSummaryParams]?: FilterItem;
  };
  loading?: boolean;
  onChange: (
    filters: {
      [x in KeyGetVisitSummaryParams]?: FilterItem;
    },
  ) => void;
  onClose: () => void;
  open: boolean;
};

const MEMBER_FILTERS: KeyGetVisitSummaryParams[] = [
  'language',
  'referralSourceId',
];

const VESTA_TEAM_FILTERS: KeyGetVisitSummaryParams[] = [
  'podId',
  'npOwnerId',
  'engagementOwnerId',
];

const CCM_FILTERS: KeyGetVisitSummaryParams[] = [
  'ccmMinutesFrom',
  'ccmMinutesTo',
];

const OPS_FILTERS: KeyGetVisitSummaryParams[] = [
  'engagementLevel',
  'excludeAppointmentTypesInPriorMonth',
];

export const VisitSummaryFilterModal = ({
  data,
  filters,
  loading,
  open,
  onChange,
  onClose,
}: Props) => {
  const [pendingFilters, setPendingFilters] = useState<
    {
      [x in KeyGetVisitSummaryParams]?: FilterItem;
    }
  >(filters || {});

  useEffect(() => {
    setPendingFilters(filters || {});
  }, [filters]);

  return (
    <Panel.FilterModal
      open={open}
      onClear={() => onChange({})}
      onClose={onClose}
      onSubmit={() => onChange(pendingFilters)}
    >
      <FilterGroup
        title={translate('components.FilterGroup.member')}
        count={getFilterCount(pendingFilters, MEMBER_FILTERS)}
      >
        <SelectLanguage
          data-cy="visit-summary-filter-modal-language"
          items={data.languages}
          label={translate('visitsSummary.filter.language')}
          limitTags={1}
          loading={loading}
          onChange={(values: Selectable[]) => {
            setPendingFilters({
              ...pendingFilters,
              language: getFilterItem(
                translate('visitsSummary.filter.language'),
                values,
              ),
            });
          }}
          size="small"
          value={(pendingFilters.language?.value as Selectable[]) || []}
        />
        <ReferralDropdown
          data-cy="visit-summary-filter-modal-referral-sources"
          items={data.referralSources}
          label={translate('visitsSummary.filter.referralSource')}
          limitTags={1}
          loading={loading}
          multiple
          onChange={(values: Selectable[]) => {
            setPendingFilters({
              ...pendingFilters,
              referralSourceId: getFilterItem(
                translate('visitsSummary.filter.referralSource'),
                values,
              ),
            });
          }}
          size="small"
          value={pendingFilters.referralSourceId?.value || []}
        />
      </FilterGroup>
      <FiltersVestaTeam
        data={{ employees: data.employees, pods: data.pods }}
        filters={pendingFilters}
        keys={VESTA_TEAM_FILTERS as VestaTeamKey[]}
        loading={loading}
        onChange={(updatedFilters) =>
          setPendingFilters({ ...pendingFilters, ...updatedFilters })
        }
      />
      <FilterGroup
        title={translate('components.FilterGroup.ccm')}
        count={getFilterCount(pendingFilters, CCM_FILTERS)}
      >
        <div className="grid-wrapper" style={{ gap: '0 1rem' }}>
          <Label className="grid-span-12">
            {translate('visitsSummary.filter.ccmMinutes')}
          </Label>
          <TextInput
            className="grid-span-6"
            maxLength={4}
            onChange={(newValue?: string) => {
              const number =
                newValue !== undefined ? parseInt(newValue, 10) : NaN;
              setPendingFilters({
                ...pendingFilters,
                ccmMinutesFrom: getFilterItem(
                  translate('visitsSummary.filter.ccmMinutesFrom'),
                  isNaN(number) ? undefined : number,
                ),
              });
            }}
            placeholder={translate('global.from')}
            value={
              (pendingFilters.ccmMinutesFrom?.value as FilterSelectable<number>)
                ?.value
            }
            size="small"
          />
          <TextInput
            className="grid-span-6"
            maxLength={4}
            onChange={(newValue?: string) => {
              const number =
                newValue !== undefined ? parseInt(newValue, 10) : NaN;
              setPendingFilters({
                ...pendingFilters,
                ccmMinutesTo: getFilterItem(
                  translate('visitsSummary.filter.ccmMinutesTo'),
                  isNaN(number) ? undefined : number,
                ),
              });
            }}
            placeholder={translate('global.to')}
            value={
              (pendingFilters.ccmMinutesTo?.value as FilterSelectable<number>)
                ?.value
            }
            size="small"
          />
        </div>
      </FilterGroup>
      <FilterGroup
        title={translate('components.FilterGroup.ops')}
        count={getFilterCount(pendingFilters, OPS_FILTERS)}
      >
        <Select
          data-cy="visit-summary-filter-modal-engagement-level"
          items={Enum.toSelectable(MemberEngagementLevel.asArray)}
          label={translate('visitsSummary.filter.engagementLevel')}
          limitTags={1}
          multiple
          onChange={(values: Selectable[]) => {
            setPendingFilters({
              ...pendingFilters,
              engagementLevel: getFilterItem(
                translate('visitsSummary.filter.engagementLevel'),
                values,
              ),
            });
          }}
          size="small"
          value={pendingFilters.engagementLevel?.value || []}
        />
        <Select
          data-cy="visit-summary-filter-modal-excluded-prio-appointments"
          items={data.appointmentTypes}
          label={translate(
            'visitsSummary.filter.excludeAppointmentTypesInPriorMonth',
          )}
          limitTags={1}
          loading={loading}
          multiple
          onChange={(values: Selectable[]) =>
            setPendingFilters({
              ...pendingFilters,
              excludeAppointmentTypesInPriorMonth: getFilterItem(
                translate(
                  'visitsSummary.filter.excludeAppointmentTypesInPriorMonth',
                ),
                values,
              ),
            })
          }
          size="small"
          value={
            pendingFilters?.excludeAppointmentTypesInPriorMonth?.value || []
          }
        />
      </FilterGroup>
    </Panel.FilterModal>
  );
};
